import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FaSpinner } from "react-icons/fa";

import { apiCall } from "@/api/shared";
import Alert from "@/components/Alert";
import H3 from "@/components/H3";
import Spacer from "@/components/Spacer";
import { logout } from "@/helpers/helpers";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";

// Module-level Set to track tokens - survives component remounts but not page refreshes
// This is the primary guard against infinite loops
const attemptedTokens = new Set();

// Also use sessionStorage as a backup for edge cases
const ATTEMPTED_TOKENS_KEY = "@tc_attempted_tokens";

function markTokenAttempted(token) {
  attemptedTokens.add(token);
  try {
    const stored = sessionStorage.getItem(ATTEMPTED_TOKENS_KEY);
    const tokens = stored ? JSON.parse(stored) : [];
    if (!tokens.includes(token)) {
      tokens.push(token);
      sessionStorage.setItem(ATTEMPTED_TOKENS_KEY, JSON.stringify(tokens));
    }
  }
  catch {
    // Ignore sessionStorage errors
  }
}

function hasTokenBeenAttempted(token) {
  if (attemptedTokens.has(token)) {
    return true;
  }
  try {
    const stored = sessionStorage.getItem(ATTEMPTED_TOKENS_KEY);
    const tokens = stored ? JSON.parse(stored) : [];
    return tokens.includes(token);
  }
  catch {
    return false;
  }
}

function TcAuth() {
  const [kycObj, setKycObj] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const searchParams = useSearch({ strict: false });
  const redirectUrl = searchParams.redirect ?? "/";
  const navigate = useNavigate();
  // Subscribe to auth changes reactively
  const accessToken = useAppStore(state => state.authentication?.access_token);
  const search = useRouterState({ select: s => s.location.searchStr });
  const token = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get("token");
  }, [search]);

  useEffect(() => {
    // Prevent multiple login attempts - token is single-use
    // Use module-level Set + sessionStorage to persist across component remounts
    if (!token || hasTokenBeenAttempted(token)) {
      if (!token) {
        setError("No TuneCore login token provided");
      }
      return;
    }

    // Mark token as attempted IMMEDIATELY before any async operations
    // This must happen synchronously to prevent race conditions
    markTokenAttempted(token);

    const performLogin = async () => {
      setPending(true);

      // Call logout first to clear previous session
      logout();
      setError(null);

      const apiUrl = useAppStore.getState().apiUrl;
      const details = await fetch(`${apiUrl}/rightsapp/tunecoreLogin`, {
        method: "POST",
        body: JSON.stringify({
          token,
        }),
      })
        .then(res => res.json())
        .catch((e) => {
          setPending(false);
          setError(`${e.name}: ${e.message}`);
        });

      setPending(false);

      if (details?.access_token) {
        useAppStore.setState({ authentication: details });
        return;
      }
      if (details?.error) {
        setError(details.error_description);
      }
    };

    performLogin();
  }, [token]);

  useEffect(() => {
    const fetchKycStatus = async () => {
      const kyc = await apiCall({
        endpoint: "/rightsapp/get",
        path: "/api/v1/user/getkyc",
      });
      // AppState.update((s) => {
      //   s.kyc = kyc;
      // });
      useAppStore.setState({ kyc });
      setKycObj(kyc);
    };
    if (accessToken) {
      fetchKycStatus();
    }
  }, [accessToken]);

  useEffect(() => {
    if (kycObj) {
      const showSecurity = kycObj.kycrequired && !kycObj.kycpassed && kycObj.messages && kycObj.messages.length > 0 && kycObj.messages[0] === "KYC Required";
      const showSecurityStep2 = kycObj.kycrequired && !kycObj.kycpassed && kycObj.messages && kycObj.messages.length > 0 && kycObj.messages[0] === "KYC still pending";
      if (showSecurity) {
        navigate({ to: "/security" });
      }
      else if (showSecurityStep2) {
        navigate({ to: "/security/begin-security-check" });
      }
      else {
        navigate({ to: redirectUrl, replace: true });
      }
    }
  }, [kycObj, navigate, redirectUrl]);
  return (
    <div className="container mx-auto">
      <div className="flex flex-col items-center justify-center p-4 text-center min-h-screen-minus-footer">
        <div className="w-full text-white lg:w-1/3">
          {pending && (
            <div className="flex flex-col justify-cente">
              <H3>Logging In</H3>
              <Spacer thin />
              <FaSpinner className="w-full animate-spin" size={36} />
            </div>
          )}
          {error && (
            <>
              <Spacer />
              <Alert msg={error} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TcAuth;
